/**
 * Размерные сетки категорий (дерево категорий v2, 27.09.2026).
 *
 * Сетка задаётся на КАТЕГОРИИ и наследуется вниз по дереву, как раздел и пол:
 * «Женщинам / Одежда» даёт женскую одежную сетку всем своим листьям, а
 * «Бельё и купальники» переопределяет её своей. Форма товара в кабинете
 * продавца берёт сетку из категории и предлагает её значения; витрина по ней
 * же выбирает шкалу фильтра размеров.
 *
 * Основное значение — российский размер (так требует большинство площадок и
 * так ищет покупатель), вторая подпись — международный. Соответствия — те же,
 * что печатает плитка размера на витрине (`api-gateway/src/data/sizeSystem.ts`):
 * женская XS = 42, мужская M = 48.
 *
 * Сетка — подсказка, а не запрет: `allowCustom` разрешает продавцу вписать
 * своё значение («Oversize», «30×40 см»). Жёстко сервер размеры не проверяет —
 * в каталоге уже лежат карточки с международными буквами, и отказ в их
 * правке был бы потерей, а не порядком.
 */
import type { SizeScale } from "./sizes"

export const SIZE_GRID_IDS = [
  "clothing-women",
  "clothing-men",
  "lingerie",
  "shoes-women",
  "shoes-men",
  "kids-clothing",
  "kids-shoes",
  "jewelry",
  "one-size",
  "volume",
] as const
export type SizeGridId = (typeof SIZE_GRID_IDS)[number]

export type SizeGridValue = {
  /** Основное значение — то, что сохраняется в размере варианта. */
  value: string
  /** Вторая подпись (международный размер), если у сетки она есть. */
  secondary?: string
}

export type SizeGrid = {
  id: SizeGridId
  /** Название сетки для продавца. */
  label: string
  /** Какие обмеры покупателя стоят за сеткой — подсказка в форме. */
  hint: string
  values: SizeGridValue[]
  /** Можно ли вписать своё значение, которого нет в сетке. */
  allowCustom: boolean
  /** Шкала фильтра размеров на витрине. */
  scale: SizeScale
}

const letters = (pairs: [string, string][]): SizeGridValue[] =>
  pairs.map(([value, secondary]) => ({ value, secondary }))

const plain = (values: string[]): SizeGridValue[] => values.map((value) => ({ value }))

const range = (from: number, to: number, step = 1): string[] => {
  const out: string[] = []
  for (let n = from; n <= to + 1e-9; n += step) out.push(Number.isInteger(n) ? String(n) : n.toFixed(1))
  return out
}

export const SIZE_GRIDS: Record<SizeGridId, SizeGrid> = {
  "clothing-women": {
    id: "clothing-women",
    label: "Женская одежда (RU / INT)",
    hint: "Обхват груди, талии и бёдер, см",
    values: letters([
      ["40", "XXS"],
      ["42", "XS"],
      ["44", "S"],
      ["46", "M"],
      ["48", "L"],
      ["50", "XL"],
      ["52", "XXL"],
      ["54", "3XL"],
      ["56", "4XL"],
    ]),
    allowCustom: true,
    scale: "clothing",
  },
  "clothing-men": {
    id: "clothing-men",
    label: "Мужская одежда (RU / INT)",
    hint: "Обхват груди, талии и бёдер, см",
    values: letters([
      ["44", "XS"],
      ["46", "S"],
      ["48", "M"],
      ["50", "L"],
      ["52", "XL"],
      ["54", "XXL"],
      ["56", "3XL"],
      ["58", "4XL"],
    ]),
    allowCustom: true,
    scale: "clothing",
  },
  lingerie: {
    id: "lingerie",
    label: "Бельё (обхват под грудью и чашка)",
    hint: "Бюстгальтер — обхват под грудью и чашка; трусы и купальники — обхват бёдер",
    values: plain([
      "70A", "70B", "70C", "70D",
      "75A", "75B", "75C", "75D",
      "80A", "80B", "80C", "80D",
      "85B", "85C", "85D",
      "XS", "S", "M", "L", "XL",
    ]),
    allowCustom: true,
    scale: "other",
  },
  "shoes-women": {
    id: "shoes-women",
    label: "Женская обувь (RU)",
    hint: "Длина стопы, см",
    values: plain(range(34, 42, 0.5).filter((v) => !v.endsWith(".5") || Number(v) >= 35)),
    allowCustom: false,
    scale: "shoes",
  },
  "shoes-men": {
    id: "shoes-men",
    label: "Мужская обувь (RU)",
    hint: "Длина стопы, см",
    values: plain(range(38, 47)),
    allowCustom: false,
    scale: "shoes",
  },
  "kids-clothing": {
    id: "kids-clothing",
    label: "Детская одежда (рост, см)",
    hint: "Рост ребёнка, см; возраст — дополнительно",
    values: plain(range(50, 164, 6).map(String)),
    allowCustom: true,
    scale: "kids",
  },
  "kids-shoes": {
    id: "kids-shoes",
    label: "Детская обувь (RU)",
    hint: "Длина стопы, см",
    values: plain(range(19, 38)),
    allowCustom: false,
    scale: "shoes",
  },
  jewelry: {
    id: "jewelry",
    label: "Украшения (размер кольца, мм)",
    hint: "Для колец — внутренний диаметр, мм; остальное — единый размер",
    values: plain(["Универсальный", ...range(15, 20, 0.5)]),
    allowCustom: true,
    scale: "other",
  },
  "one-size": {
    id: "one-size",
    label: "Единый размер",
    hint: "Размера нет или он один; габариты — в блоке «Доставка»",
    values: plain(["Универсальный"]),
    allowCustom: true,
    scale: "universal",
  },
  volume: {
    id: "volume",
    label: "Объём или вес",
    hint: "Объём (мл) или вес (г) упаковки",
    values: plain(["5 мл", "10 мл", "30 мл", "50 мл", "100 мл", "200 мл", "50 г", "100 г"]),
    allowCustom: true,
    scale: "volume",
  },
}

export const isSizeGridId = (value: unknown): value is SizeGridId =>
  typeof value === "string" && (SIZE_GRID_IDS as readonly string[]).includes(value)

/** Сетка по id; неизвестный или пустой id — `undefined` (форма покажет свободный ввод). */
export const sizeGridOf = (id: string | null | undefined): SizeGrid | undefined =>
  isSizeGridId(id) ? SIZE_GRIDS[id] : undefined

/** Подсказка шкалы фильтра по сетке категории. */
export const sizeScaleForGrid = (id: string | null | undefined): SizeScale | undefined =>
  sizeGridOf(id)?.scale

/**
 * Мерки тела для таблицы размеров: обхваты груди, талии и бёдер в сантиметрах
 * для русского размера. Это стандартная российская размерная таблица (борд
 * `3664:70380`); её печатают витрина («Таблица размеров») и окно «Размерная
 * сетка» в форме товара. Женская и мужская таблицы разные, как и сами сетки.
 * У ростовок, обуви и белья колонки другие — мерок здесь нет, и таблица
 * показывает только значения сетки.
 */
export type SizeMeasurements = {
  /** Обхват груди, см — «78-82». */
  chest: string
  /** Обхват талии, см. */
  waist: string
  /** Обхват бёдер, см. */
  hips: string
}

export const SIZE_MEASUREMENTS: Partial<Record<SizeGridId, Record<string, SizeMeasurements>>> = {
  "clothing-women": {
    "40": { chest: "78-82", waist: "60-64", hips: "86-90" },
    "42": { chest: "82-86", waist: "64-68", hips: "90-94" },
    "44": { chest: "86-90", waist: "68-72", hips: "94-98" },
    "46": { chest: "90-94", waist: "72-76", hips: "98-102" },
    "48": { chest: "94-98", waist: "76-80", hips: "102-106" },
    "50": { chest: "98-102", waist: "80-84", hips: "106-110" },
    "52": { chest: "102-107", waist: "84-89", hips: "110-114" },
    "54": { chest: "107-112", waist: "89-94", hips: "114-118" },
    "56": { chest: "112-117", waist: "94-99", hips: "118-122" },
  },
  "clothing-men": {
    "44": { chest: "86-89", waist: "74-77", hips: "90-93" },
    "46": { chest: "90-93", waist: "78-81", hips: "94-97" },
    "48": { chest: "94-97", waist: "82-85", hips: "98-101" },
    "50": { chest: "98-101", waist: "86-89", hips: "102-105" },
    "52": { chest: "102-105", waist: "90-93", hips: "106-109" },
    "54": { chest: "106-109", waist: "94-97", hips: "110-113" },
    "56": { chest: "110-113", waist: "98-101", hips: "114-117" },
    "58": { chest: "114-117", waist: "102-105", hips: "118-121" },
  },
}

/** Мерки русского размера в сетке; у сетки без мерок — `undefined`. */
export const sizeMeasurementsOf = (
  gridId: string | null | undefined,
  ruLabel: string,
): SizeMeasurements | undefined => (isSizeGridId(gridId) ? SIZE_MEASUREMENTS[gridId]?.[ruLabel] : undefined)
